import { createApp } from 'vue'
import { createPinia } from 'pinia'
import App from './App.vue'
import router from './router'
import ElementPlus from 'element-plus'
import 'element-plus/dist/index.css'
import 'element-plus/theme-chalk/dark/css-vars.css'
import * as ElementPlusIconsVue from '@element-plus/icons-vue'
import './assets/styles/index.scss'
import zhCn from 'element-plus/dist/locale/zh-cn.mjs'
import 'vant/lib/index.css'
import { setupVant } from './plugins/vant'
import { getToken, validateToken, clearAllAuthState } from '@/utils/auth'
import directives from './directive' // 导入指令
import permission from './directive/permission' // 导入权限指令
import './assets/darkmode.css' // 导入暗黑模式样式
import { useUserStore } from './stores/user'
import { formatDateTime } from '@/utils/format'   // 日期格式化工具

console.log('开始初始化应用:', {
  env: process.env.NODE_ENV,
  time: new Date().toLocaleString()
})

const app = createApp(App)
const pinia = createPinia()
app.use(pinia)

// 注册 Vant 组件
setupVant(app)

// 兼容 Vant v4 移除 Toast.fail/success 与 Dialog.confirm
import { Toast as VantToast, showFailToast, showSuccessToast, Dialog as VantDialog, showConfirmDialog } from 'vant'
if (!VantToast.fail) {
  VantToast.fail = (msg) => showFailToast(msg)
}
if (!VantToast.success) {
  VantToast.success = (msg) => showSuccessToast(msg)
}
// 兼容 Dialog.confirm -> showConfirmDialog
if (!VantDialog.confirm) {
  VantDialog.confirm = (options) => showConfirmDialog(options)
}

// 全局错误处理
app.config.errorHandler = (err, instance, info) => {
  console.error('全局错误:', err)
  console.error('错误组件:', instance)
  console.error('错误信息:', info)
}

// 初始化认证状态
async function initializeAuth() {
  console.log('应用启动: 检查认证状态');
  
  const token = getToken();
  if (token) {
    console.log('应用启动: 检测到有效token，尝试验证');
    
    try {
      const isValid = await validateToken();
      if (!isValid) {
        console.log('应用启动: token验证失败，清除认证状态');
        clearAllAuthState();
        
        // 如果当前路由需要认证，重定向到登录页
        if (router.currentRoute.value.meta.requiresAuth) {
          router.push('/login');
        }
      } else {
        console.log('应用启动: token验证成功');
      }
    } catch (error) {
      console.error('应用启动: token验证出错', error);
    }
  } else {
    console.log('应用启动: 未检测到token');
  }
}

// 应用启动时初始化认证状态
initializeAuth();

// 注册组件
app.use(router)
app.use(ElementPlus, {
  locale: zhCn
})

// 注册所有图标
for (const [key, component] of Object.entries(ElementPlusIconsVue)) {
  app.component(key, component)
}

app.use(directives) // 注册指令

// 注册权限指令
app.directive('permission', permission)

// 添加路由错误处理
router.onError((error) => {
  console.error('路由错误:', error)
})

// 添加全局未捕获Promise错误处理
window.addEventListener('unhandledrejection', event => {
  console.error('未处理的Promise错误:', event.reason)
})

// 抑制ResizeObserver Loop错误
const resizeObserverError = /ResizeObserver loop completed with undelivered notifications/;
window.addEventListener('error', (event) => {
  if (resizeObserverError.test(event.message)) {
    event.stopImmediatePropagation();
  }
});

// 初始化多标签页同步
const userStore = useUserStore()
userStore.initMultiTabSync()

app.config.globalProperties.formatDateTime = formatDateTime
// 以后模板里直接 {{ formatDateTime(date) }} 不再受组件 methods 影响

// 应用挂载
app.mount('#app')
console.log('应用挂载完成')

// 添加全局调试函数
window.$debug = {
  // 获取所有路由
  getRoutes: () => {
    return router.getRoutes()
  },
  
  // 获取特定路由
  getRoutesByPath: (path) => {
    return router.getRoutes().filter(r => r.path.includes(path))
  },
  
  // 获取采购申请单路由
  getPurchaseRoutes: () => {
    return router.getRoutes().filter(r => r.path.includes('purchase-requests'))
  },
  
  // 获取当前路由信息
  getCurrentRoute: () => {
    return router.currentRoute.value
  },
  
  // 获取路由实例
  getRouter: () => {
    return router
  },
  
  // 打印路由信息
  printRoutes: () => {
    console.log('所有路由:', router.getRoutes().map(r => ({ path: r.path, name: r.name })))
  },
  // 查看当前用户权限
  perms: () => {
    const store = useUserStore()
    console.log('当前权限列表:', store.permissions)
    return store.permissions
  }
}

console.log('全局调试函数已添加，使用 $debug 访问')
console.log('可用方法:')
console.log('- $debug.getRoutes() - 获取所有路由')
console.log('- $debug.getRoutesByPath("purchase-requests") - 获取特定路径的路由')
console.log('- $debug.getPurchaseRoutes() - 获取采购申请单路由')
console.log('- $debug.getCurrentRoute() - 获取当前路由')
console.log('- $debug.printRoutes() - 打印所有路由信息')

app.mixin({
  methods: { formatDateTime }
})
